import type { UseFormReturn } from 'react-hook-form';
import { AlertCircle, Info } from 'lucide-react';
import { useSipInterfaces } from '../../../../hooks/useSipInterfaces';
import { isValidIpRangeList, findInvalidIpRangeEntries } from '../../../../utils/ip-validation';
import type { SipPeerFormData } from '../schema';
import { PREFERRED_PROTOCOL_BY_TYPE } from '../schema';

/** Interface tab: SIP interface selection + source IP ranges. */
export function InterfaceTab({ form }: { form: UseFormReturn<SipPeerFormData> }) {
  const peerType = form.watch('type');
  const { data: sipInterfaces, isLoading: sipInterfacesLoading } = useSipInterfaces();

  // Filter interfaces: prefer protocol matching peer type (teams→TLS, carrier→UDP/TLS, etc.)
  const preferredProtocols = PREFERRED_PROTOCOL_BY_TYPE[peerType] || ['UDP', 'TCP', 'TLS'];
  const compatibleInterfaces = sipInterfaces?.filter(iface => {
    if (!iface.enabled) return false;
    return preferredProtocols.includes(iface.protocol as 'UDP' | 'TCP' | 'TLS');
  }) || [];

  // SIP Interface is required for all peers
  const requiresInterface = true;
  const requiresSourceIp = ['carrier', 'teams'].includes(peerType);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">SIP Interface Configuration</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select the SIP interface this peer will use for communication. A SIP interface is required for all peers.
        </p>
      </div>

      {/* SIP Interface Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SIP Interface {requiresInterface && <span className="text-red-500">*</span>}
        </label>
        <select
          {...form.register('sipInterfaceId')}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={sipInterfacesLoading}
        >
          <option value="">
            {sipInterfacesLoading
              ? 'Loading interfaces...'
              : 'Select an interface (required)'}
          </option>
          {compatibleInterfaces.map((iface) => (
            <option key={iface.id} value={iface.id}>
              {iface.interfaceId} - {iface.protocol}:{iface.port}
              {iface.description ? ` (${iface.description})` : ''}
            </option>
          ))}
        </select>
        {compatibleInterfaces.length === 0 && !sipInterfacesLoading && (
          <p className="mt-2 text-sm text-amber-600 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            No {preferredProtocols.join('/')} interfaces found for {peerType}. Create one in Network Settings first.
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          The interface determines which port and protocol the SBC uses to communicate with this peer.
        </p>
      </div>

      {/* Source IP Ranges */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source IP Ranges {requiresSourceIp ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(optional)</span>}
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Restrict incoming traffic to specific IP ranges (CIDR notation). Example: 10.0.0.0/24, 192.168.1.1/32. At least one of domain or source IP ranges is required so the peer can be identified.
          {requiresSourceIp && ' Required for carrier and Teams peers.'}
        </p>
        <textarea
          {...form.register('sourceIpRanges', {
            onChange: () => form.clearErrors('sourceIpRanges'),
            validate: (value) => {
              const raw = typeof value === 'string' ? value : '';
              if (!isValidIpRangeList(raw)) {
                const invalid = findInvalidIpRangeEntries(raw);
                return `Invalid IP/CIDR entry: ${invalid.join(', ')}. Use IPv4 (10.0.0.1) or CIDR (10.0.0.0/24).`;
              }
              return true;
            },
          })}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
            form.formState.errors.sourceIpRanges
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
          }`}
          rows={3}
          placeholder="Enter IP ranges (one per line or comma-separated)"
        />
        {form.formState.errors.sourceIpRanges && (
          <p className="mt-1 text-sm text-red-600">{form.formState.errors.sourceIpRanges.message}</p>
        )}
      </div>

      {/* Info box for Teams */}
      {peerType === 'teams' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Microsoft Teams Requirements
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Teams Direct Routing requires a TLS interface with a valid certificate.
                  Ensure your interface is configured with a certificate trusted by Microsoft.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
